// activityEvents.js
// Purpose: Record lightweight user activity events (e.g., app_open) to Supabase.
// - Uses authenticated supabase client (anon/auth flow) so RLS applies.
// - Dedupes per foreground transition via a short TTL stored in sessionStorage.
// - Retries quietly with exponential backoff and logs warnings without blocking UI.

import { supabase } from '../config/supabase';
import logger from './logger';

/**
 * Get the session start timestamp for duration tracking.
 * @returns {number|null} Timestamp in milliseconds, or null if not set
 */
export function getSessionStartTime() {
  try {
    const key = 'timegenie_session_start_ts';
    const val = sessionStorage.getItem(key);
    return val ? Number(val) : null;
  } catch (e) {
    return null;
  }
}

/**
 * Get or create a UUID session_id used for analytics sessions.
 * Stored per tab in sessionStorage so it resets on tab close.
 * This is distinct from chat session IDs and matches DB uuid type.
 * @returns {string} uuid string
 */
export function getUuidSessionId() {
  try {
    const key = 'timegenie_uuid_session_id';
    let val = sessionStorage.getItem(key);
    if (val && typeof val === 'string' && val.trim()) return val;
    let id = '';
    try {
      // @ts-ignore
      id =
        (globalThis.crypto && globalThis.crypto.randomUUID && globalThis.crypto.randomUUID()) || '';
    } catch (_) {
      // no-op
    }
    if (!id) {
      // Not ideal for uuid column, but preserved to avoid throw; DB may reject if strict.
      id = '00000000-0000-4000-8000-' + Math.random().toString(16).slice(2, 14).padEnd(12, '0');
    }
    sessionStorage.setItem(key, id);
    return id;
  } catch (e) {
    // Fallback best-effort value
    return '00000000-0000-4000-8000-' + Math.random().toString(16).slice(2, 14).padEnd(12, '0');
  }
}

/**
 * Clear the current UUID session_id.
 */
export function clearUuidSessionId() {
  try {
    sessionStorage.removeItem('timegenie_uuid_session_id');
  } catch (_) {
    // ignore
  }
}

/**
 * Record a session_start event with a fresh uuid session_id.
 * Also sets the session start timestamp for duration tracking.
 * @param {string} userId
 */
export async function recordSessionStart(userId) {
  try {
    if (!userId) return true;
    const now = Date.now();
    setSessionStartTime(now);
    const sessionId = getUuidSessionId();
    /** @type {Record<string, any>} */
    const client = {
      platform: typeof navigator !== 'undefined' ? navigator.platform || 'web' : 'web',
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
      locale:
        (typeof navigator !== 'undefined' && (navigator.language || navigator.languages?.[0])) ||
        'en-US',
      app_session_id: getAppSessionId(),
      app_version: process.env.REACT_APP_VERSION || process.env.REACT_APP_APP_VERSION || undefined,
    };
    if (client.app_version === undefined) delete client.app_version;

    const { error } = await supabase
      .from('user_activity_events')
      .insert([
        {
          user_id: userId,
          session_id: sessionId,
          event_type: 'session_start',
          client,
          meta: { route: typeof window !== 'undefined' ? window.location?.pathname : '' },
        },
      ])
      .select('id')
      .single();
    if (error) logger.warn('recordSessionStart: failed', error);
    return true;
  } catch (e) {
    logger.warn('recordSessionStart: unexpected error', e);
    return true;
  }
}

/**
 * Record a session_end event for the current uuid session_id with duration.
 * Clears the uuid session_id after successful write.
 * @param {string} userId
 */
export async function recordSessionEnd(userId) {
  try {
    if (!userId) return true;
    const now = Date.now();
    const startTime = getSessionStartTime();
    const durationSecs = startTime ? Math.max(0, Math.round((now - startTime) / 1000)) : undefined;
    const sessionId = getUuidSessionId();

    /** @type {Record<string, any>} */
    const client = {
      platform: typeof navigator !== 'undefined' ? navigator.platform || 'web' : 'web',
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
      locale:
        (typeof navigator !== 'undefined' && (navigator.language || navigator.languages?.[0])) ||
        'en-US',
      app_session_id: getAppSessionId(),
      app_version: process.env.REACT_APP_VERSION || process.env.REACT_APP_APP_VERSION || undefined,
    };
    if (client.app_version === undefined) delete client.app_version;

    /** @type {Record<string, any>} */
    const meta = { route: typeof window !== 'undefined' ? window.location?.pathname : '' };
    if (typeof durationSecs === 'number') meta.duration_secs = durationSecs;

    const { error } = await supabase
      .from('user_activity_events')
      .insert([
        {
          user_id: userId,
          session_id: sessionId,
          event_type: 'session_end',
          client,
          meta,
        },
      ])
      .select('id')
      .single();

    if (!error) {
      clearSessionStartTime();
      clearUuidSessionId();
    } else {
      logger.warn('recordSessionEnd: failed', error);
    }
    return true;
  } catch (e) {
    logger.warn('recordSessionEnd: unexpected error', e);
    return true;
  }
}

/**
 * Set the session start timestamp for duration tracking.
 * @param {number} timestamp - Timestamp in milliseconds
 */
export function setSessionStartTime(timestamp) {
  try {
    const key = 'timegenie_session_start_ts';
    sessionStorage.setItem(key, String(timestamp));
  } catch (e) {
    // Best-effort; ignore errors
  }
}

/**
 * Clear the session start timestamp.
 */
export function clearSessionStartTime() {
  try {
    const key = 'timegenie_session_start_ts';
    sessionStorage.removeItem(key);
  } catch (e) {
    // Best-effort; ignore errors
  }
}

/**
 * Generate or retrieve a per-tab app session ID.
 * Stored in sessionStorage so it's unique per tab and cleared on tab close.
 * @returns {string}
 */
export function getAppSessionId() {
  try {
    const key = 'timegenie_app_session_id';
    let val = sessionStorage.getItem(key);
    if (val && typeof val === 'string' && val.trim()) return val;
    // Prefer crypto.randomUUID when available
    let id = '';
    try {
      // @ts-ignore - randomUUID may not exist in older browsers
      id =
        (globalThis.crypto && globalThis.crypto.randomUUID && globalThis.crypto.randomUUID()) || '';
    } catch (_) {
      // no-op
    }
    if (!id) {
      id = `sess-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    }
    sessionStorage.setItem(key, id);
    return id;
  } catch (e) {
    // Fallback if storage is unavailable
    return `sess-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
}

/**
 * Record an app_open event when the app returns to foreground.
 * - Requires an authenticated user; otherwise it no-ops.
 * - Dedupes very recent calls (default 2s TTL) to avoid double fires across listeners.
 * @param {string} userId
 * @param {{ sessionId?: string|null, metadata?: Record<string, any>, dedupeTtlMs?: number }} [opts]
 * @returns {Promise<boolean>} true if attempted (or intentionally deduped), false only on hard failure to enqueue
 */
export async function recordAppOpen(userId, opts = {}) {
  try {
    if (!userId) return true; // nothing to do

    const dedupeKey = 'timegenie_last_app_open_ts';
    const now = Date.now();
    const ttl = typeof opts.dedupeTtlMs === 'number' ? opts.dedupeTtlMs : 2000;
    try {
      const last = Number(sessionStorage.getItem(dedupeKey) || '0');
      if (last && now - last < ttl) {
        // Deduped
        return true;
      }
      sessionStorage.setItem(dedupeKey, String(now));
    } catch (_) {
      // Best-effort; continue
    }

    // Track session start time for duration calculation
    if (!getSessionStartTime()) {
      setSessionStartTime(now);
    }

    // Build minimal payload. Do NOT include first_name/last_name (DB trigger populates).
    // Note: session_id in DB is uuid type, so we only pass chat session IDs (which are UUIDs).
    // App session IDs (string format) are stored in client.app_session_id instead.
    const chatSessionId = opts.sessionId || null; // This should be a UUID from chat
    const appSessionId = getAppSessionId(); // This is a string like "sess-..."

    /** @type {Record<string, any>} */
    const client = {
      platform: typeof navigator !== 'undefined' ? navigator.platform || 'web' : 'web',
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
      locale:
        (typeof navigator !== 'undefined' && (navigator.language || navigator.languages?.[0])) ||
        'en-US',
      app_session_id: appSessionId, // Store string session ID here
      // Inject app version if provided at build time; otherwise omit downstream.
      app_version: process.env.REACT_APP_VERSION || process.env.REACT_APP_APP_VERSION || undefined,
    };
    if (client.app_version === undefined) delete client.app_version;

    /** @type {Record<string, any>} */
    const meta = {
      ...opts.metadata,
    };

    const event = {
      user_id: userId,
      session_id: chatSessionId, // Only pass UUID chat session IDs, or null
      event_type: 'app_open',
      client, // Renamed from metadata to match DB schema
      meta, // User-provided metadata goes here
      // occurred_at: omitted to use DB default
    };

    // Retry quietly up to 3 times with exponential backoff
    let attempt = 0;
    let lastErr = null;
    while (attempt < 3) {
      // eslint-disable-next-line no-await-in-loop
      const { error } = await supabase
        .from('user_activity_events')
        .insert([event])
        .select('id')
        .single();
      if (!error) return true;
      lastErr = error;
      attempt += 1;
      const delay = 300 * Math.pow(2, attempt - 1); // 300ms, 600ms, 1200ms
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, delay));
    }
    logger.warn('recordAppOpen: failed to insert after retries', lastErr);
    return true; // treat as handled (we won't block app flow)
  } catch (e) {
    logger.warn('recordAppOpen: unexpected error', e);
    return true; // non-blocking
  }
}

/**
 * Record an app_close event when the app goes to background or closes.
 * Calculates session duration from the stored session start time.
 * - Requires an authenticated user; otherwise it no-ops.
 * - Includes duration_secs in meta if session start time is available.
 * @param {string} userId
 * @param {{ sessionId?: string|null, metadata?: Record<string, any> }} [opts]
 * @returns {Promise<boolean>} true if attempted, false only on hard failure to enqueue
 */
export async function recordAppClose(userId, opts = {}) {
  try {
    if (!userId) return true; // nothing to do

    const now = Date.now();
    const startTime = getSessionStartTime();

    // Calculate duration if we have a start time
    let durationSecs = null;
    if (startTime && typeof startTime === 'number' && startTime > 0) {
      durationSecs = Math.round((now - startTime) / 1000);
    }

    // Build minimal payload
    const chatSessionId = opts.sessionId || null;
    const appSessionId = getAppSessionId();

    /** @type {Record<string, any>} */
    const client = {
      platform: typeof navigator !== 'undefined' ? navigator.platform || 'web' : 'web',
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
      locale:
        (typeof navigator !== 'undefined' && (navigator.language || navigator.languages?.[0])) ||
        'en-US',
      app_session_id: appSessionId,
      app_version: process.env.REACT_APP_VERSION || process.env.REACT_APP_APP_VERSION || undefined,
    };
    if (client.app_version === undefined) delete client.app_version;

    /** @type {Record<string, any>} */
    const meta = {
      ...opts.metadata,
    };

    // Add duration to meta if available
    if (durationSecs !== null) {
      meta.duration_secs = durationSecs;
    }

    const event = {
      user_id: userId,
      session_id: chatSessionId,
      event_type: 'app_close',
      client,
      meta,
    };

    // Use fetch with keepalive for reliability during page unload
    // keepalive ensures the request completes even if the page unloads
    try {
      if (typeof navigator !== 'undefined') {
        const blob = new Blob([JSON.stringify(event)], { type: 'application/json' });
        const endpoint = `${process.env.REACT_APP_SUPABASE_URL}/rest/v1/user_activity_events`;

        // Get the user's session token for proper authentication
        const {
          data: { session },
        } = await supabase.auth.getSession();
        const accessToken = session?.access_token || process.env.REACT_APP_SUPABASE_ANON_KEY || '';

        const headers = {
          apikey: process.env.REACT_APP_SUPABASE_ANON_KEY || '',
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          Prefer: 'return=minimal',
        };

        // sendBeacon doesn't support custom headers directly, so we fall back to fetch
        // with keepalive for unload scenarios
        const response = await fetch(endpoint, {
          method: 'POST',
          headers,
          body: JSON.stringify(event),
          keepalive: true, // Important for unload events
        });

        if (response.ok) {
          // Clear session start time after successful close event
          clearSessionStartTime();
          return true;
        }
      }
    } catch (e) {
      logger.debug?.('recordAppClose: sendBeacon/fetch failed, trying Supabase client', e);
    }

    // Fallback to regular Supabase client (may not complete during unload)
    const { error } = await supabase
      .from('user_activity_events')
      .insert([event])
      .select('id')
      .single();

    if (!error) {
      // Clear session start time after successful close event
      clearSessionStartTime();
      return true;
    }

    logger.warn('recordAppClose: failed to insert', error);
    return true; // treat as handled (we won't block app flow)
  } catch (e) {
    logger.warn('recordAppClose: unexpected error', e);
    return true; // non-blocking
  }
}
